<template>
  <v-app class="login-page" :class="{ 'theme--dark': isDarkMode }">
    <v-main class="login-main" :style="{ background: isDarkMode ? '#121212' : 'linear-gradient(135deg, #003F74 0%, #00264a 100%)' }">
      <v-container fluid class="login-container fill-height">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="5" lg="4" xl="3">
            <!-- Theme Toggle -->
            <div class="theme-toggle-container">
              <v-btn icon @click="toggleTheme" class="theme-toggle" dark>
                <v-icon>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
              </v-btn>
            </div>

            <!-- Login Card -->
            <v-card class="login-card" :color="isDarkMode ? 'surface' : 'white'" elevation="12">
              <!-- Logo Section -->
              <div class="login-header">
                <v-avatar size="80" class="login-logo-avatar">
                  <img :src="logo" alt="Kes Lakay">
                </v-avatar>
                <h1 class="login-title">KES LAKAY</h1>
                <p class="login-subtitle">Connectez-vous à votre compte</p>
              </div>

              <v-card-text class="login-form-container">
                <v-form @submit.prevent="submit">
                  <!-- Username Field -->
                  <v-text-field
                    v-model="form.username"
                    label="Nom d'utilisateur"
                    prepend-inner-icon="mdi-account-outline"
                    outlined
                    dense
                    class="login-field"
                    :disabled="loading"
                    autocomplete="username"
                  ></v-text-field>

                  <!-- Password Field -->
                  <v-text-field
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    label="Mot de passe"
                    prepend-inner-icon="mdi-lock-outline"
                    :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append="showPassword = !showPassword"
                    outlined
                    dense
                    class="login-field"
                    :disabled="loading"
                    autocomplete="current-password"
                  ></v-text-field>

                  <!-- Error Alert -->
                  <v-alert
                    v-if="login_errors"
                    type="error"
                    dense
                    dismissible
                    class="login-error"
                    @input="hideAlert"
                  >
                    {{ error_message }}
                  </v-alert>

                  <!-- Submit Button -->
                  <v-btn
                    type="submit"
                    block
                    large
                    color="secondary"
                    class="login-btn"
                    :loading="loading"
                    :disabled="loading || !form.username || !form.password"
                  >
                    <v-icon left>mdi-login</v-icon>
                    Se connecter
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>

            <!-- Footer -->
            <div class="login-footer">
              <span class="white--text">{{ new Date().getFullYear() }} — Kes Lakay</span>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { mapActions } from "vuex";
import logo from "../assets/img/logo.png"

export default {
  name: 'LoginView',
  data: () => ({
    logo: logo,
    form: {
      username: "",
      password: "",
      serial_number: '000-0000-000-0000'
    },
    loading: false,
    login_errors: false,
    showPassword: false,
    error_message: 'Vérifiez votre nom d\'utilisateur et votre mot de passe puis réessayez.'
  }),
  computed: {
    isDarkMode() {
      return this.$store.getters['theme/isDarkMode'];
    }
  },
  watch: {
    isDarkMode: {
      immediate: true,
      handler(val) {
        this.$vuetify.theme.dark = val;
      }
    }
  },
  methods: {
    ...mapActions(["LogIn"]),
    async submit() {
      this.loading = true;
      this.login_errors = false;
      
      try {
        const User = {
          username: this.form.username,
          password: this.form.password
        };
        await this.LogIn(User);
        this.$router.push("/settings");
      } catch (error) {
        this.login_errors = true;
      } finally {
        this.loading = false;
      }
    },
    hideAlert() {
      this.login_errors = false;
    },
    toggleTheme() {
      this.$store.dispatch('theme/toggleTheme');
    }
  }
}
</script>

<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
}

.login-main {
  min-height: 100vh;
  transition: background 0.3s ease;
}

.login-container {
  padding: 24px;
}

.theme-toggle-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.theme-toggle {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
}

.login-card {
  border-radius: 16px !important;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.login-header {
  text-align: center;
  padding: 40px 24px 24px;
  background: linear-gradient(135deg, rgba(0, 63, 116, 0.05) 0%, rgba(246, 141, 58, 0.05) 100%);
}

.login-logo-avatar {
  background: #FFFFFF;
  padding: 12px;
  margin-bottom: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  border: 3px solid #F68D3A;
  
  img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
}

.login-title {
  font-size: 24px;
  font-weight: 700;
  color: #003F74;
  margin: 0 0 8px;
  letter-spacing: 2px;
}

.theme--dark .login-title {
  color: #4A90D9;
}

.login-subtitle {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
}

.theme--dark .login-subtitle {
  color: rgba(255, 255, 255, 0.6);
}

.login-form-container {
  padding: 32px !important;
}

.login-field {
  margin-bottom: 8px;
  
  ::v-deep .v-input__slot {
    border-radius: 10px !important;
  }
}

.login-error {
  margin-bottom: 16px;
  border-radius: 10px !important;
}

.login-btn {
  margin-top: 16px;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  letter-spacing: 0.5px;
  height: 48px !important;
}

.login-footer {
  text-align: center;
  margin-top: 32px;
  opacity: 0.8;
  font-size: 13px;
}

// Responsive Styles
@media (max-width: 599px) {
  .login-container {
    padding: 16px;
    align-items: flex-start;
    padding-top: 40px;
  }
  
  .login-card {
    border-radius: 16px !important;
    max-width: 100%;
  }
  
  .login-header {
    padding: 32px 20px 20px;
  }
  
  .login-logo-avatar {
    width: 70px !important;
    height: 70px !important;
    padding: 8px;
    margin-bottom: 12px;
  }
  
  .login-title {
    font-size: 20px;
    letter-spacing: 1.5px;
  }
  
  .login-subtitle {
    font-size: 12px;
  }
  
  .login-form-container {
    padding: 20px !important;
  }
  
  .login-btn {
    font-size: 14px !important;
    height: 44px !important;
  }
  
  .login-footer {
    margin-top: 24px;
    font-size: 11px;
  }
  
  .theme-toggle {
    top: 12px;
    right: 12px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .login-container {
    padding: 24px;
  }
  
  .login-card {
    max-width: 420px;
  }
  
  .login-header {
    padding: 36px 24px 24px;
  }
  
  .login-form-container {
    padding: 28px !important;
  }
}
</style>